import React, { useState, useEffect } from "react";
import { database, ref, push, onValue, update, remove } from "./firebase";
import "./BillingSystem.css";
import BossNavigation from "./BossNavigation";

export default function Enterprises() {
  const [entries, setEntries] = useState([]);
  const [name, setName] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [coolJarOut, setCoolJarOut] = useState("");
  const [coolJarIn, setCoolJarIn] = useState("");
  const [coolJarFull, setCoolJarFull] = useState("");
  const [jarOut, setJarOut] = useState("");
  const [jarIn, setJarIn] = useState("");
  const [jarFull, setJarFull] = useState("");
  const [amount, setAmount] = useState("");
  const [balance, setBalance] = useState("");
  const [editId, setEditId] = useState(null);

  // --- Read entries from Firebase safely, with fallbacks and robust sorting ---
  useEffect(() => {
    const entriesRef = ref(database, "enterprisesHistory");
    const unsubscribe = onValue(entriesRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.keys(data).map((id) => {
          const entry = data[id] || {};
          return {
            id,
            name: entry.name || "",
            date: entry.date || new Date().toISOString().split("T")[0], // fallback
            coolJarOut: Number(entry.coolJarOut) || 0,
            coolJarIn: Number(entry.coolJarIn) || 0,
            coolJarFull: Number(entry.coolJarFull) || 0,
            jarOut: Number(entry.jarOut) || 0,
            jarIn: Number(entry.jarIn) || 0,
            jarFull: Number(entry.jarFull) || 0,
            amount: Number(entry.amount) || 0,
            balance: Number(entry.balance) || 0,
            isPaid: entry.isPaid === true || Number(entry.balance) <= 0,
          };
        });

        // safe sort by date (entries with invalid dates fallback to 0)
        list.sort((a, b) => {
          const ta = new Date(a.date).getTime() || 0;
          const tb = new Date(b.date).getTime() || 0;
          return tb - ta;
        });

        setEntries(list);
      } else {
        setEntries([]);
      }
    });

    return () => {
      // unsubscribe from realtime listener
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, []);

  // --- helpers ---
  const resetForm = () => {
    setName("");
    setDate(new Date().toISOString().split("T")[0]);
    setCoolJarOut("");
    setCoolJarIn("");
    setCoolJarFull("");
    setJarOut("");
    setJarIn("");
    setJarFull("");
    setAmount("");
    setBalance("");
    setEditId(null);
  };

  const handleSave = () => {
    if (!name.trim()) {
      alert("Please enter name first!");
      return;
    }

    // ensure date is present
    const recordDate = date || new Date().toISOString().split("T")[0];
    const enteredBalance = balance === "" ? 0 : Number(balance);
    const isPaid = enteredBalance <= 0;

    const record = {
      name: name.trim(),
      date: recordDate,
      coolJarOut: Number(coolJarOut) || 0,
      coolJarIn: Number(coolJarIn) || 0,
      coolJarFull: Number(coolJarFull) || 0,
      jarOut: Number(jarOut) || 0,
      jarIn: Number(jarIn) || 0,
      jarFull: Number(jarFull) || 0,
      amount: Number(amount) || 0,
      balance: enteredBalance,
      isPaid,
    };

    if (editId) {
      update(ref(database, `enterprisesHistory/${editId}`), record)
        .then(() => {
          alert("✅ Entry updated successfully!");
          resetForm();
        })
        .catch((err) => {
          console.error("Update failed:", err);
          alert("❌ Update failed. Check console.");
        });
    } else {
      push(ref(database, "enterprisesHistory"), record)
        .then(() => {
          alert("💾 Entry saved successfully!");
          resetForm();
        })
        .catch((err) => {
          console.error("Save failed:", err);
          alert("❌ Save failed. Check console.");
        });
    }
  };

  const handleEdit = (entry) => {
    setEditId(entry.id);
    setName(entry.name || "");
    setDate(entry.date || new Date().toISOString().split("T")[0]);
    setCoolJarOut(entry.coolJarOut ?? "");
    setCoolJarIn(entry.coolJarIn ?? "");
    setCoolJarFull(entry.coolJarFull ?? "");
    setJarOut(entry.jarOut ?? "");
    setJarIn(entry.jarIn ?? "");
    setJarFull(entry.jarFull ?? "");
    setAmount(entry.amount ?? "");
    setBalance(entry.balance ?? "");
  };

  const handleDelete = (id) => {
    if (!window.confirm("🗑️ Delete this entry?")) return;
    remove(ref(database, `enterprisesHistory/${id}`))
      .then(() => alert("Deleted"))
      .catch((err) => {
        console.error("Delete failed:", err);
        alert("Delete failed. Check console.");
      });
  };

  const handleMarkPaid = (id) => {
    if (!window.confirm("✅ Mark this entry as paid?")) return;
    update(ref(database, `enterprisesHistory/${id}`), { balance: 0, isPaid: true })
      .then(() => alert("Marked as paid"))
      .catch((err) => {
        console.error("Mark paid failed:", err);
        alert("Operation failed. Check console.");
      });
  };

  // Group entries by date safely
  const groupedEntries = entries.reduce((acc, e) => {
    const key = e.date || "Unknown Date";
    if (!acc[key]) acc[key] = [];
    acc[key].push(e);
    return acc;
  }, {});

  return (
    <div className="history-container">
      <h2 className="page-title">🏭 Enterprises Entry</h2>
      <BossNavigation />

      {/* Input Form */}
      <div className="form-section">
        <table className="styled-table" style={{ maxWidth: 980 }}>
          <thead>
            <tr>
              <th style={{ width: "40%" }}>Product</th>
              <th style={{ width: "30%" }}>Quantity</th>
              <th style={{ width: "30%" }}>Full</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Cool Jar Out</td>
              <td>
                <input
                  type="number"
                  value={coolJarOut}
                  onChange={(e) => setCoolJarOut(e.target.value)}
                  className="input-small"
                />
              </td>
              <td></td>
            </tr>

            <tr>
              <td>Cool Jar In</td>
              <td>
                <input
                  type="number"
                  value={coolJarIn}
                  onChange={(e) => setCoolJarIn(e.target.value)}
                  className="input-small"
                />
              </td>
              <td>
                <input
                  type="number"
                  value={coolJarFull}
                  onChange={(e) => setCoolJarFull(e.target.value)}
                  className="input-small"
                />
              </td>
            </tr>

            <tr>
              <td>Jar Out</td>
              <td>
                <input
                  type="number"
                  value={jarOut}
                  onChange={(e) => setJarOut(e.target.value)}
                  className="input-small"
                />
              </td>
              <td></td>
            </tr>

            <tr>
              <td>Jar In</td>
              <td>
                <input
                  type="number"
                  value={jarIn}
                  onChange={(e) => setJarIn(e.target.value)}
                  className="input-small"
                />
              </td>
              <td>
                <input
                  type="number"
                  value={jarFull}
                  onChange={(e) => setJarFull(e.target.value)}
                  className="input-small"
                />
              </td>
            </tr>
          </tbody>
        </table>

        <div className="entry-controls" style={{ marginTop: 12 }}>
          <input
            type="text"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input-text"
          />

          <input
            type="number"
            placeholder="Amount (₹)"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="input-small"
          />

          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="input-date"
          />

          <input
            type="number"
            placeholder="Balance (₹)"
            value={balance}
            onChange={(e) => setBalance(e.target.value)}
            className="input-small"
          />

          <button className="save-btn" onClick={handleSave}>
            {editId ? "✏️ Update Entry" : "💾 Save Entry"}
          </button>
        </div>
      </div>

      
      {/* History */}
      <h3 className="section-title">📜 Enterprises History</h3>
      {Object.keys(groupedEntries).length === 0 ? (
        <p>No entries yet.</p>
      ) : (
        Object.keys(groupedEntries).map((dateKey) => (
          <div key={dateKey} className="history-table-section">
            <h4 className="date-header">{dateKey}</h4>
            <table className="styled-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Cool Jar Out</th>
                  <th>Cool Jar In</th>
                  <th>Jar Out</th>
                  <th>Jar In</th>
                  <th>Amount (₹)</th>
                  <th>Balance (₹)</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedEntries[dateKey].map((entry) => (
                  <tr key={entry.id}>
                    <td style={{ fontWeight: "bold" }}>{entry.name}</td>
                    <td>{entry.coolJarOut}</td>
                    <td>
                      {entry.coolJarIn}
                      {entry.coolJarFull > 0 ? ` (${entry.coolJarFull} full)` : ""}
                    </td>
                    <td>{entry.jarOut}</td>
                    <td>
                      {entry.jarIn}
                      {entry.jarFull > 0 ? ` (${entry.jarFull} full)` : ""}
                    </td>
                    <td>₹{entry.amount}</td>
                    <td style={{ color: entry.isPaid ? "green" : "red", fontWeight: 700 }}>
                      ₹{entry.balance}
                    </td>
                    <td>{entry.isPaid ? <span style={{ color: "green" }}>✅ Paid</span> : <span style={{ color: "red" }}>⏳ Pending</span>}</td>
                    <td>
                      <button className="edit-btn" onClick={() => handleEdit(entry)}>✏️ Edit</button>
                      <button className="delete-btn" onClick={() => handleDelete(entry.id)}>🗑️ Delete</button>
                      {!entry.isPaid && <button className="paid-btn" onClick={() => handleMarkPaid(entry.id)}>💰 Mark Paid</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
}
